import React, { useState, useRef, useEffect } from "react";
import DSButton from "./Button";

/**
 * DS MenuButton — 버튼 + 액션 팝오버 (선택값을 유지하지 않는 액션 메뉴).
 *
 * DSDropdown 은 select(값 선택·표시)라 액션 메뉴에 부적합해 분리.
 * 팝오버 스타일은 기존 .ds-dropdown-popover 재사용.
 * 사용처: 주문 발주 툴바 [수동 발주 ▾] → 단건 발주 / 복수 발주.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - 버튼 라벨
 * @param {Array<{label: React.ReactNode, onClick: () => void, disabled?: boolean}>} props.items
 * @param {"primary"|"secondary"|"tertiary"|"danger"} [props.variant="secondary"]
 * @param {"xs"|"sm"|"md"|"lg"} [props.size="md"]
 * @param {React.ReactNode} [props.icon]
 * @param {boolean} [props.disabled]
 */
const DSMenuButton = ({
  children,
  items = [],
  variant = "secondary",
  size = "md",
  icon,
  disabled,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className={`ds-menu-button ${className}`}>
      <DSButton
        variant={variant}
        size={size}
        icon={icon}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        {children}
      </DSButton>
      {open && (
        <div className="ds-dropdown-popover">
          {items.map((item, idx) => (
            <button
              key={idx}
              type="button"
              className="ds-dropdown-popover__item"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(DSMenuButton);
